import { MapPin } from "lucide-react"
import { useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import {
  type OnboardingAddressErrors,
  OnboardingAddressFieldset,
} from "@/components/onboarding/onboarding-address-fieldset"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"

export interface RedeemProfileAddress {
  recipient_name: string | null
  street: string | null
  street_line_2: string | null
  postal_code: string | null
  city: string | null
  country: string | null
}

interface RedeemShipToProps {
  /** The creator's own profile address, or null when they have none. */
  profileAddress: RedeemProfileAddress | null
  /** The Inertia render-prop errors object, passed whole. */
  errors: OnboardingAddressErrors
}

/**
 * OKS-985 — the Ship-to step inside the redeem sheet (UX Spec §2.1).
 *
 * # Placement
 *
 * Rendered BELOW the coin ledger, never above it. OKS-557's above-the-fold
 * rule is not renegotiated here: the cost is the first decision, the
 * destination is the second.
 *
 * # ⚠️ The fieldset is REUSED, not re-implemented
 *
 * `OnboardingAddressFieldset` emits `shipping_address[...]` names and reads
 * dotted `shipping_address.*` error keys. Both endpoints that consume this
 * form expect exactly that shape. A local copy of the six inputs would be a
 * second wire format for one fieldset — the kind that typechecks, renders,
 * submits, and is silently ignored by the server.
 *
 * # ⚠️ The defensive branch (UX Spec §1, amended)
 *
 * "No profile address" expands the fieldset from the start with a lead line.
 * PO's amendment is explicit that this is NOT a reachable user journey — the
 * onboarding enforcer's address step is unconditional, so a creator who can
 * open `/rewards` has a profile address. It is built anyway because the gate
 * that makes it unreachable lives in a different subsystem and a later ticket
 * could relax it without knowing this flow depended on it. Build it; do not
 * count it as coverage of a creator-facing path.
 *
 * # ⚠️ Save-to-profile defaults are ASYMMETRIC and never implicit
 *
 * Checked when there is no profile address (otherwise the creator is asked
 * again on every claim — the dead end this ticket exists to remove).
 * Unchecked when overriding an existing one: someone deliberately typing
 * "send it to my mum's" must not silently rewrite their profile. Always a
 * visible checkbox, never inferred behaviour, and it never animates or
 * changes on its own — a default that moves by itself is a consent bug.
 */
export function RedeemShipTo({ profileAddress, errors }: RedeemShipToProps) {
  const { t } = useTranslation()
  const hasProfileAddress = Boolean(profileAddress?.street)

  // Expanded from the start when there is nothing to prefill.
  const [editing, setEditing] = useState(!hasProfileAddress)
  const toggleRef = useRef<HTMLButtonElement>(null)
  const fieldsetRef = useRef<HTMLDivElement>(null)

  // Any dotted key means the server refused on the address; expand so the
  // creator can see which field is flagged rather than a collapsed block with
  // an error they cannot reach.
  const hasAddressError = Object.keys(errors).some((key) =>
    key.startsWith("shipping_address."),
  )
  const expanded = editing || hasAddressError

  const summary = [
    profileAddress?.street,
    profileAddress?.street_line_2,
    [profileAddress?.postal_code, profileAddress?.city]
      .filter(Boolean)
      .join(" "),
    profileAddress?.country,
  ]
    .filter(Boolean)
    .join(", ")

  return (
    <div
      className="bg-muted/40 mt-3 rounded-xl p-3"
      data-testid="redeem-sheet-ship-to"
    >
      <div className="text-muted-foreground flex items-center gap-1.5 text-xs font-medium">
        <MapPin className="size-3.5" aria-hidden="true" />
        {t("rewards.creator.ship_to.label")}
      </div>

      {/* ⚠️ THE COLLAPSED STATE MUST STILL SUBMIT THE ADDRESS.
          The fieldset is the only thing carrying `shipping_address[...]`
          inputs, and it is not rendered while collapsed — so without these
          hidden fields the PREFILLED happy path posts NO address at all, the
          server refuses the claim on six presence errors, and the creator is
          told their own confirmed address is invalid.

          That is the primary path in PRD §1 ("one tap more than a perks
          claim"), and it was broken: found by LQE's E2E, missed by my own
          Vitest probe because the probe passed an `errors` object, which
          forced `expanded` true and tested the branch that was never in
          doubt. A probe that cannot enter the failing state cannot see the
          failure. */}
      {hasProfileAddress && !expanded && (
        <>
          <input
            type="hidden"
            name="shipping_address[recipient_name]"
            value={profileAddress?.recipient_name ?? ""}
          />
          <input
            type="hidden"
            name="shipping_address[street]"
            value={profileAddress?.street ?? ""}
          />
          <input
            type="hidden"
            name="shipping_address[street_line_2]"
            value={profileAddress?.street_line_2 ?? ""}
          />
          <input
            type="hidden"
            name="shipping_address[postal_code]"
            value={profileAddress?.postal_code ?? ""}
          />
          <input
            type="hidden"
            name="shipping_address[city]"
            value={profileAddress?.city ?? ""}
          />
          <input
            type="hidden"
            name="shipping_address[country]"
            value={profileAddress?.country ?? ""}
          />
        </>
      )}

      {hasProfileAddress && !expanded && (
        <div className="mt-2" data-testid="redeem-sheet-ship-to-summary">
          <p className="text-sm font-semibold break-words">
            {profileAddress?.recipient_name}
          </p>
          <p className="text-muted-foreground text-sm break-words">{summary}</p>
        </div>
      )}

      {hasProfileAddress && !expanded && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          ref={toggleRef}
          className="mt-2 px-0"
          data-testid="redeem-sheet-use-different-address"
          onClick={() => {
            setEditing(true)
            // UX §4 — focus moves to the first input on expand.
            window.requestAnimationFrame(() => {
              fieldsetRef.current
                ?.querySelector<HTMLInputElement>("input, select")
                ?.focus()
            })
          }}
        >
          {t("rewards.creator.ship_to.use_different")}
        </Button>
      )}

      {expanded && (
        <div className="mt-2" ref={fieldsetRef}>
          {!hasProfileAddress && (
            <p
              className="text-muted-foreground mb-2 text-sm"
              data-testid="redeem-sheet-ship-to-lead"
            >
              {t("rewards.creator.ship_to.lead")}
            </p>
          )}

          <OnboardingAddressFieldset
            values={{
              recipient_name: profileAddress?.recipient_name ?? "",
              street: profileAddress?.street ?? "",
              street_line_2: profileAddress?.street_line_2 ?? "",
              postal_code: profileAddress?.postal_code ?? "",
              city: profileAddress?.city ?? "",
              country: profileAddress?.country ?? "",
            }}
            errors={errors}
          />

          <div className="mt-3 flex items-center gap-2">
            <Checkbox
              id="redeem-save-to-profile"
              name="save_to_profile"
              value="1"
              defaultChecked={!hasProfileAddress}
              data-testid="redeem-sheet-save-to-profile"
            />
            {/* Bound by htmlFor — never a bare tick (UX §6). */}
            <Label
              htmlFor="redeem-save-to-profile"
              className="text-sm font-normal"
            >
              {t("rewards.creator.ship_to.save_to_profile")}
            </Label>
          </div>
        </div>
      )}
    </div>
  )
}
